import { Asset } from '@/api/generatedApi';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { FileUpload } from '@/services/fileUpload';
import { useFileUpload } from '@/services/fileUpload/useFileUpload';
import { useTenants } from '@/services/tenants/useTenants';
import { isAsset } from '@/utils/assets';
import { Cancel } from '@mui/icons-material';
import { Box, Button, styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { OpenInNewTabMenuItem } from '@/components/ContextMenu/OpenInNewTabMenuItem';
import { Thumbnail } from '../../Thumbnail';
import { ThumbnailPreview } from '../../ThumbnailPreview';
import { MatchPopoverButton } from '../MatchPopoverButton';
import { SearchInput } from '../SearchInput';

const THUMBNAIL_SIZE = 64;
const PREVIEW_SIZE = 144;

const AssetSearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '6px 0 0 0',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '6px',
}));

const ThumbnailContainer = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  position: 'relative',
  width: THUMBNAIL_SIZE,
  height: THUMBNAIL_SIZE,
  backgroundColor: theme.palette.action.hover,
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover button': {
    visibility: 'visible',
  },
}));

const XButton = styled(Button)(({ theme }) => ({
  padding: '0',
  minWidth: '0',
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  backgroundColor: theme.palette.text.secondary,
  position: 'absolute',
  top: '2px',
  right: '2px',
  zIndex: 1,
  visibility: 'hidden',
  '&:hover': {
    backgroundColor: theme.palette.text.primary,
  },
}));

const MatchSelectionDetails = ({
  asset,
  fileUpload,
  onClearAsset,
  onDeleteAsset,
  onClearUploadedFile,
}: {
  asset?: Asset;
  fileUpload?: FileUpload;
  onClearAsset: () => void;
  onDeleteAsset: () => void;
  onClearUploadedFile: (index: number) => void;
}) => {
  const { isAssetSearch, isImageSearch } = useSearchFilters();
  const [contextMenuPosition, setContextMenuPosition] = useState<{ left: number; top: number } | undefined>();

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuPosition({ left: event.clientX, top: event.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenuPosition(undefined);
  };

  if (!isAssetSearch && !isImageSearch) {
    return;
  }

  const renderThumbnail = () => {
    if (isAssetSearch && asset) {
      return (
        <ThumbnailPreview
          showToolbar
          primaryAsset={asset}
          placement='right'
          size={PREVIEW_SIZE}
          onDelete={onDeleteAsset}
          followCursor={false}
        >
          <ThumbnailContainer onContextMenu={handleContextMenu}>
            <XButton onClick={onClearAsset} size='small'>
              <Cancel sx={{ color: 'white', fontSize: '20px' }} />
            </XButton>
            <Thumbnail asset={asset} size={THUMBNAIL_SIZE} />
          </ThumbnailContainer>
        </ThumbnailPreview>
      );
    }
    if (isImageSearch && fileUpload?.files && fileUpload.files.length > 0) {
      return fileUpload.files.map((file, index) => (
        <ThumbnailPreview
          key={`${file.name}-${index}`}
          file={file}
          placement='right'
          size={PREVIEW_SIZE}
          followCursor={false}
        >
          <ThumbnailContainer>
            <XButton onClick={() => onClearUploadedFile(index)} size='small'>
              <Cancel sx={{ color: 'white', fontSize: '20px' }} />
            </XButton>
            <Thumbnail file={file} size={THUMBNAIL_SIZE} />
          </ThumbnailContainer>
        </ThumbnailPreview>
      ));
    }
    return <Box />;
  };

  return (
    <>
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
        gap='12px'
        flex='1'
        data-testid='search-selection-details'
      >
        <Box display='flex' flexDirection='row' alignItems='center' flexWrap='wrap' gap='6px' marginLeft='6px'>
          {renderThumbnail()}
        </Box>
      </Box>
      {contextMenuPosition && asset && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          <OpenInNewTabMenuItem item={{ ...asset, contentType: 'asset' }} onClose={handleCloseContextMenu} />
        </BaseContextMenu>
      )}
    </>
  );
};

export const SearchInputSection = ({
  searchQuery,
  onSearch,
}: {
  searchQuery?: string;
  onSearch: (query: string) => void;
}) => {
  const { getPath } = useTenants();
  const navigate = useNavigate();
  const { fileUpload, setFileUpload: setFileUploadContext, removeFile } = useFileUpload();
  const { updateSearchParameters, clearAssetSearch, assetId, clearAssetSearchAndSetQuery } = useSearchFilters();
  const { asset: assetFromUrl } = useGetAsset({ assetId: assetId ?? '' });

  const [asset, setAsset] = useState<Asset | undefined>();
  const fileInputReference = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (assetFromUrl) {
      setAsset(assetFromUrl);
    } else if (!assetId) {
      setAsset(undefined);
    }
  }, [assetFromUrl, assetId]);

  const handleMatchSelection = (match: Asset | FileUpload) => {
    if (isAsset(match)) {
      setAsset(match);
      updateSearchParameters({ assetId: match.id, searchType: match.type === 'image' ? 'visual' : 'geometric' });
    } else {
      setAsset(undefined);
      clearAssetSearch();
      if (match.files && match.files.length > 0) {
        setFileUploadContext({ files: match.files, useCase: 'visual-search' });
      }
      navigate(getPath('search'));
      updateSearchParameters({ searchType: 'image' });
    }
  };

  const handleClearAsset = () => {
    setAsset(undefined);
    clearAssetSearchAndSetQuery(searchQuery ?? '');
  };

  const handleClearUploadedFile = (fileIndex: number) => {
    if (!fileUpload?.files) {
      return;
    }

    const fileToRemove = fileUpload.files[fileIndex];
    if (!fileToRemove) {
      return;
    }

    removeFile(fileToRemove);

    if (fileUpload.files.length === 1) {
      setFileUploadContext({ files: undefined, useCase: undefined });

      if (fileInputReference.current) {
        fileInputReference.current.value = '';
      }

      clearAssetSearchAndSetQuery(searchQuery ?? '');
    }
  };

  const handleDeleteAsset = () => {
    setAsset(undefined);
    clearAssetSearchAndSetQuery(searchQuery ?? '');
  };

  return (
    <AssetSearchBox>
      <SearchInput
        placeholder='Search'
        initialValue={searchQuery ?? ''}
        onSearch={onSearch}
        SearchAttachment={
          <MatchSelectionDetails
            asset={asset}
            onDeleteAsset={handleDeleteAsset}
            onClearAsset={handleClearAsset}
            onClearUploadedFile={handleClearUploadedFile}
            fileUpload={fileUpload}
          />
        }
        AddToSearchButton={
          <MatchPopoverButton
            onMatchSelection={handleMatchSelection}
            fileInputRef={fileInputReference}
            hasFileSelected={!!asset || !!fileUpload?.files?.length}
            hasAssetSelected={!!asset}
          />
        }
      />
    </AssetSearchBox>
  );
};
